import { useRef, useState } from 'react'
import { Box, Flex, Icon, IconButton, Input, Text, VStack } from '@chakra-ui/react'
import { Trash04Icon } from '../../../assets/icons'

interface ModalFileInputProps {
  label?: string
  accept?: string
  maxSizeMB?: number
  detail?: string
  value: File | null
  onChange: (file: File | null) => void
  required?: boolean
  isInvalid?: boolean
  error?: string
}

const ModalFileInput: React.FC<ModalFileInputProps> = ({
  label,
  accept,
  maxSizeMB = 10,
  detail,
  value,
  onChange,
  required,
  isInvalid,
  error,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const validate = (file: File): string | null => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File size exceeds ${maxSizeMB} MB.`
    }
    if (accept) {
      const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase())
      const fileExt = `.${file.name.split('.').pop()?.toLowerCase() || ''}`
      const fileType = file.type.toLowerCase()
      const matches = acceptedTypes.some(
        (a) => a === fileType || a === fileExt || (a.endsWith('/*') && fileType.startsWith(a.replace('/*', '/')))
      )
      if (!matches) {
        const labels = acceptedTypes
          .map((a) => (a.startsWith('.') ? a.toUpperCase().slice(1) : a))
          .join(', ')
        return `Accepted formats: ${labels}`
      }
    }
    return null
  }

  const [validationError, setValidationError] = useState('')

  const handleFile = (file: File | null) => {
    if (!file) {
      onChange(null)
      setValidationError('')
      return
    }
    const err = validate(file)
    if (err) {
      setValidationError(err)
      onChange(null)
      return
    }
    setValidationError('')
    onChange(file)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0] || null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0] || null
    handleFile(file)
  }

  const displayError = error || validationError

  return (
    <VStack align="left" spacing="4px" width="100%">
      {label && (
        <Text textStyle="l-xs" color={required ? 'solid.700' : 'solid.500'} fontWeight={required ? '600' : undefined}>
          {label}
          {required && <Text as="span" color="primary.100"> *</Text>}
        </Text>
      )}

      {value ? (
        /* ── Selected file preview ── */
        <Flex
          align="center"
          gap="12px"
          p="12px 16px"
          bg="solid.25"
          borderRadius="8px"
          border="1px solid"
          borderColor="solid.100"
        >
          <Box flex="1" minW={0}>
            <Text textStyle="l-xs" fontWeight="600" color="solid.900" noOfLines={1}>
              {value.name}
            </Text>
            <Text textStyle="b-xs" color="solid.400">
              {formatSize(value.size)}
            </Text>
          </Box>
          <IconButton
            aria-label="Remove file"
            variant="ghost"
            size="xs"
            minW="28px"
            h="28px"
            borderRadius="6px"
            color="solid.400"
            _hover={{ bg: 'error.0', color: 'error.100' }}
            onClick={() => handleFile(null)}
            icon={
              <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none">
                <Trash04Icon />
              </Icon>
            }
          />
        </Flex>
      ) : (
        /* ── Drop zone ── */
        <Box
          position="relative"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor={displayError ? 'error.100' : isDragging ? 'primary.100' : 'solid.100'}
          borderRadius="8px"
          bg={isDragging ? 'primary.0' : 'white'}
          transition="all 0.15s ease"
          _hover={{ borderColor: 'solid.200', bg: 'solid.25' }}
          cursor="pointer"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <Flex direction="column" align="center" py="16px" px="12px" pointerEvents="none">
            <Text textStyle="b-xs" color="solid.700">
              Drop or{' '}
              <Text as="span" fontWeight="600" color="primary.100" textDecoration="underline">
                click here
              </Text>{' '}
              to upload
            </Text>
            <Text textStyle="b-xs" color="solid.400" mt="2px">
              {detail ?? `Max ${maxSizeMB} MB`}
            </Text>
          </Flex>
          <Input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            position="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            opacity="0"
            cursor="pointer"
          />
        </Box>
      )}

      {displayError && (
        <Text textStyle="b-xs" color="error.100">
          {displayError}
        </Text>
      )}
    </VStack>
  )
}

export default ModalFileInput
